import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

export interface ActionOutcome {
  outcome: 'success' | 'failed' | 'cancelled';
  detail?: string;
}

export interface ActionHandler {
  id: string;
  // Idempotent handlers (navigate, change-password, …) can be clicked
  // multiple times without state divergence; non-idempotent ones
  // (contact-support) are marked "used" after the first success so the
  // user doesn't accidentally file a duplicate ticket.
  isIdempotent: boolean;
  run(params: Record<string, unknown>): Promise<ActionOutcome>;
}

@Injectable({ providedIn: 'root' })
export class ActionRegistryService {
  private readonly router = inject(Router);

  private readonly handlers: Record<string, ActionHandler> = {
    'contact-support': {
      id: 'contact-support',
      isIdempotent: false,
      run: async (params) => {
        const subject = typeof params['subject'] === 'string' ? (params['subject'] as string) : 'Support request';
        const href = `mailto:support@supplycanvas.com?subject=${encodeURIComponent(subject)}`;
        window.location.href = href;
        return { outcome: 'success' };
      },
    },
    'change-password': {
      id: 'change-password',
      isIdempotent: true,
      run: async () => {
        const ok = await this.router.navigate(['/user'], { fragment: 'password' });
        return { outcome: ok ? 'success' : 'failed' };
      },
    },
    'change-language': {
      id: 'change-language',
      isIdempotent: true,
      run: async () => {
        const ok = await this.router.navigate(['/user'], { fragment: 'language' });
        return { outcome: ok ? 'success' : 'failed' };
      },
    },
    'manage-notifications': {
      id: 'manage-notifications',
      isIdempotent: true,
      run: async () => {
        const ok = await this.router.navigate(['/user'], { fragment: 'newsletter' });
        return { outcome: ok ? 'success' : 'failed' };
      },
    },
    navigate: {
      id: 'navigate',
      isIdempotent: true,
      run: async (params) => {
        const path = typeof params['path'] === 'string' ? (params['path'] as string) : '';
        if (!path) return { outcome: 'failed', detail: 'missing path' };
        const ok = await this.router.navigateByUrl(path);
        return { outcome: ok ? 'success' : 'failed' };
      },
    },
  };

  resolve(actionId: string): ActionHandler | null {
    if (actionId.startsWith('navigate-')) return this.handlers['navigate'];
    return this.handlers[actionId] ?? null;
  }
}
